(() => {
  const registry = window.ccT10IISOutput || {
    instances: new WeakMap(),
    lifecycleBound: false
  };

  window.ccT10IISOutput = registry;

  const SELECTOR = '[data-cc-t10-iis]';
  const MOBILE_QUERY = '(max-width: 768px)';
  const SWIPE_THRESHOLD = 12;
  const DIRECTION_RATIO = 1.15;
  const SYNTHETIC_CLICK_WINDOW = 400;

  const findSection = (target) => {
    if (!target) {
      return null;
    }

    if (target.matches?.(SELECTOR)) {
      return target;
    }

    return target.querySelector?.(SELECTOR) || null;
  };

  const initSection = (section) => {
    if (!section || registry.instances.has(section)) {
      return;
    }

    const image = section.querySelector('[data-cc-t10-iis-image]');
    const card = section.querySelector('[data-cc-t10-iis-swipe-surface]');
    const controls = Array.from(section.querySelectorAll('[data-cc-t10-iis-control]'));
    const status = section.querySelector('[data-cc-t10-iis-status]');
    const payload = section.querySelector('[data-cc-t10-iis-data]');

    if (!image || !card || controls.length !== 8 || !payload) {
      return;
    }

    let modes = [];

    try {
      modes = JSON.parse(payload.textContent || '[]').filter((mode) => mode?.url);
    } catch (error) {
      modes = [];
    }

    if (modes.length !== 8) {
      return;
    }

    const cleanupTasks = [];
    const decodedCache = new Map();
    const mobileMedia = window.matchMedia(MOBILE_QUERY);
    let activeIndex = 0;
    let intendedIndex = 0;
    let requestToken = 0;
    let destroyed = false;
    let pointerState = null;
    let preloadObserver = null;
    let revealObserver = null;
    let idleHandle = null;
    let idleUsesTimeout = false;
    let lastTouchActivation = null;

    const addCleanup = (task) => cleanupTasks.push(task);

    const addListener = (target, eventName, handler, options) => {
      target?.addEventListener(eventName, handler, options);
      addCleanup(() => target?.removeEventListener(eventName, handler, options));
    };

    const resolveTarget = (targetIndex, source) => {
      if (!Number.isInteger(targetIndex)) {
        return null;
      }

      if (source === 'swipe') {
        return (targetIndex + modes.length) % modes.length;
      }

      if (source === 'keyboard') {
        return Math.min(modes.length - 1, Math.max(0, targetIndex));
      }

      return targetIndex >= 0 && targetIndex < modes.length ? targetIndex : null;
    };

    const ensureDecoded = (index) => {
      const mode = modes[index];

      if (!mode?.url) {
        return Promise.reject(new Error('Missing IIS mode image URL.'));
      }

      const cached = decodedCache.get(mode.url);

      if (cached) {
        return cached;
      }

      const promise = new Promise((resolve, reject) => {
        const preloader = new Image();
        let settled = false;

        const finish = (isReady) => {
          if (settled) {
            return;
          }

          settled = true;
          preloader.removeEventListener('load', handleLoad);
          preloader.removeEventListener('error', handleError);

          if (isReady && preloader.naturalWidth > 0) {
            resolve({ url: mode.url, width: preloader.naturalWidth, height: preloader.naturalHeight });
            return;
          }

          decodedCache.delete(mode.url);
          reject(new Error(`Unable to load IIS mode image ${index + 1}.`));
        };

        const handleLoad = async () => {
          if (typeof preloader.decode === 'function') {
            try {
              await preloader.decode();
            } catch (error) {
              // A completed load with natural dimensions is the approved decode fallback.
            }
          }

          finish(preloader.naturalWidth > 0);
        };

        const handleError = () => finish(false);

        preloader.decoding = 'async';
        preloader.addEventListener('load', handleLoad, { once: true });
        preloader.addEventListener('error', handleError, { once: true });
        preloader.src = mode.url;

        if (preloader.complete) {
          handleLoad();
        }
      });

      decodedCache.set(mode.url, promise);
      return promise;
    };

    const updateControls = () => {
      controls.forEach((control, index) => {
        if (index === activeIndex) {
          control.setAttribute('aria-current', 'true');
        } else {
          control.removeAttribute('aria-current');
        }
      });
    };

    const navigate = async (targetIndex, source) => {
      const normalizedTarget = resolveTarget(targetIndex, source);

      if (normalizedTarget === null || destroyed) {
        return;
      }

      intendedIndex = normalizedTarget;
      const token = ++requestToken;

      if (normalizedTarget === activeIndex) {
        section.removeAttribute('aria-busy');
        return;
      }

      section.setAttribute('aria-busy', 'true');

      try {
        await ensureDecoded(normalizedTarget);
      } catch (error) {
        if (token === requestToken) {
          section.removeAttribute('aria-busy');
        }
        return;
      }

      if (destroyed || token !== requestToken || normalizedTarget !== intendedIndex) {
        return;
      }

      const mode = modes[normalizedTarget];
      image.src = mode.url;
      image.alt = mode.alt;
      activeIndex = normalizedTarget;
      updateControls();
      section.removeAttribute('aria-busy');

      if (status && source !== 'initial') {
        status.textContent = mode.status || `目前顯示 IIS 輸出配置模式 ${normalizedTarget + 1}`;
      }
    };

    const activateControl = (control, source) => {
      const index = Number(control?.dataset.ccIndex);

      if (Number.isInteger(index)) {
        void navigate(index, source);
      }
    };

    controls.forEach((control) => {
      addListener(control, 'pointerup', (event) => {
        if (event.pointerType !== 'touch' && event.pointerType !== 'pen') {
          return;
        }

        event.preventDefault();
        lastTouchActivation = { control, time: Date.now() };
        activateControl(control, 'click');
      });

      addListener(control, 'click', (event) => {
        if (
          lastTouchActivation &&
          lastTouchActivation.control === control &&
          Date.now() - lastTouchActivation.time < SYNTHETIC_CLICK_WINDOW
        ) {
          return;
        }

        activateControl(control, event.detail === 0 ? 'keyboard' : 'click');
      });
    });

    addListener(section, 'keydown', (event) => {
      if (!event.target.matches?.('[data-cc-t10-iis-control]') || !window.matchMedia('(min-width: 769px)').matches) {
        return;
      }

      let targetIndex = null;

      switch (event.key) {
        case 'ArrowLeft':
          targetIndex = activeIndex - 1;
          break;
        case 'ArrowRight':
          targetIndex = activeIndex + 1;
          break;
        case 'Home':
          targetIndex = 0;
          break;
        case 'End':
          targetIndex = modes.length - 1;
          break;
        case 'Enter':
        case ' ':
          targetIndex = Number(event.target.dataset.ccIndex);
          break;
        default:
          return;
      }

      event.preventDefault();
      void navigate(targetIndex, 'keyboard');
    });

    const releasePointer = () => {
      if (pointerState?.captured && card.hasPointerCapture?.(pointerState.pointerId)) {
        card.releasePointerCapture(pointerState.pointerId);
      }

      pointerState = null;
    };

    addListener(card, 'pointerdown', (event) => {
      if (!mobileMedia.matches || !event.isPrimary || event.button !== 0) {
        return;
      }

      pointerState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        deltaX: 0,
        state: 'undecided',
        captured: false
      };
    });

    addListener(card, 'pointermove', (event) => {
      if (!pointerState || event.pointerId !== pointerState.pointerId) {
        return;
      }

      const deltaX = event.clientX - pointerState.startX;
      const deltaY = event.clientY - pointerState.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      pointerState.deltaX = deltaX;

      if (pointerState.state === 'undecided') {
        if (absY >= SWIPE_THRESHOLD && absY >= absX / DIRECTION_RATIO) {
          releasePointer();
          return;
        }

        if (absX >= SWIPE_THRESHOLD && absX > absY * DIRECTION_RATIO) {
          pointerState.state = 'horizontal';
          card.setPointerCapture?.(event.pointerId);
          pointerState.captured = true;
        }
      }

      if (pointerState?.state === 'horizontal' && event.cancelable) {
        event.preventDefault();
      }
    });

    addListener(card, 'pointerup', (event) => {
      if (!pointerState || event.pointerId !== pointerState.pointerId) {
        return;
      }

      const shouldNavigate = pointerState.state === 'horizontal' && Math.abs(pointerState.deltaX) >= SWIPE_THRESHOLD;
      const delta = pointerState.deltaX < 0 ? 1 : -1;
      releasePointer();

      if (shouldNavigate) {
        void navigate(activeIndex + delta, 'swipe');
      }
    });

    addListener(card, 'pointercancel', releasePointer);
    addListener(card, 'lostpointercapture', () => {
      if (pointerState?.captured) {
        pointerState = null;
      }
    });

    const preloadRemaining = () => {
      [2, 3, 4, 5, 6].forEach((index) => {
        void ensureDecoded(index).catch(() => {});
      });
    };

    const scheduleRemainingPreload = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleUsesTimeout = false;
        idleHandle = window.requestIdleCallback(preloadRemaining, { timeout: 1800 });
      } else {
        idleUsesTimeout = true;
        idleHandle = window.setTimeout(preloadRemaining, 240);
      }
    };

    const reveal = () => section.classList.add('is-cc-revealed');

    if (!section.dataset.ccRevealEnabled || section.dataset.ccRevealEnabled === 'false') {
      reveal();
    } else if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      reveal();
    } else {
      revealObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        reveal();
        revealObserver?.disconnect();
        revealObserver = null;
      }, { threshold: 0.24, rootMargin: '0px 0px -24% 0px' });

      revealObserver.observe(section);
    }

    if ('IntersectionObserver' in window) {
      preloadObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        scheduleRemainingPreload();
        preloadObserver?.disconnect();
        preloadObserver = null;
      }, { threshold: 0.01, rootMargin: '100% 0px 100% 0px' });

      preloadObserver.observe(section);
    } else {
      scheduleRemainingPreload();
    }

    if (image.complete && image.naturalWidth > 0) {
      decodedCache.set(modes[0].url, Promise.resolve({
        url: modes[0].url,
        width: image.naturalWidth,
        height: image.naturalHeight
      }));
    } else {
      void ensureDecoded(0).catch(() => {});
    }

    void ensureDecoded(1).catch(() => {});
    void ensureDecoded(7).catch(() => {});
    updateControls();

    const instance = {
      navigate,
      getState: () => ({ activeIndex, intendedIndex, requestToken, destroyed }),
      destroy: () => {
        destroyed = true;
        requestToken += 1;
        releasePointer();
        revealObserver?.disconnect();
        preloadObserver?.disconnect();

        if (idleHandle !== null) {
          if (idleUsesTimeout) {
            window.clearTimeout(idleHandle);
          } else {
            window.cancelIdleCallback?.(idleHandle);
          }
        }

        cleanupTasks.forEach((task) => task());
        decodedCache.clear();
        registry.instances.delete(section);
      }
    };

    registry.instances.set(section, instance);
  };

  registry.init = initSection;

  if (!registry.lifecycleBound) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = findSection(event.target);
      initSection(section);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const section = findSection(event.target);
      registry.instances.get(section)?.destroy();
    });

    registry.lifecycleBound = true;
  }

  const initAll = () => document.querySelectorAll(SELECTOR).forEach(initSection);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
